import type { DashboardProvider } from "@/api.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { plural } from "@/lib/plural.js"
import { cn } from "@/lib/utils.js"
import { count, dayMonth, duration, share } from "./format.js"

/**
 * Every source over the window, from the calls the core recorded: how often it
 * was asked, how much of it our own copy answered, and the trend of that share
 * day by day — the hybrid's reach, which is the number *Monitoring* watches to
 * see a base being learned or a scan going wrong.
 *
 * **Local is a share of the calls that answered.** A call that consulted
 * nothing — no key, no session — is neither local nor remote, the same rule
 * the health screen keeps, so an unconfigured source never reads as a
 * perfect one.
 */
export function Sources({ providers }: { providers: readonly DashboardProvider[] }) {
  return (
    <Table>
      <thead>
        <tr className="whitespace-nowrap">
          <Th>Źródło</Th>
          <Th className="text-right">Lokalnie</Th>
          <Th>Trend</Th>
          <Th className="text-right">Nie pytano</Th>
          <Th className="text-right">Błędy</Th>
          <Th className="text-right">p95</Th>
        </tr>
      </thead>
      <tbody>
        {providers.map((provider) => {
          const answered = provider.calls - provider.none
          return (
            <Tr key={provider.providerId}>
              {/*
               * How often it was asked rides under the name: every decision asks
               * every source it covers, so the column would repeat one number
               * down the table and say only where a source was not asked at all.
               */}
              <Td className="min-w-[12rem]">
                <p className="text-foreground">{provider.title}</p>
                <p className="text-[12px] text-muted-foreground">
                  <span className="font-mono">{provider.providerId}</span> ·{" "}
                  <span className="tabular-nums">{count(provider.calls)}</span>{" "}
                  {plural(provider.calls, "wywołanie", "wywołania", "wywołań")}
                </p>
              </Td>
              {/* The register's own share is the complement, so it rides in the hint. */}
              <Td
                className="text-right font-medium tabular-nums"
                {...(answered === 0
                  ? {}
                  : {
                      title: `${count(answered - provider.api)} z naszej kopii, ${count(provider.api)} z rejestru`,
                    })}
              >
                {share(answered - provider.api, answered) ?? (
                  <span className="font-normal text-muted-foreground">—</span>
                )}
              </Td>
              <Td>
                <Sparkline provider={provider} />
              </Td>
              <Td
                className={cn(
                  "text-right tabular-nums",
                  provider.none === 0 ? "text-muted-foreground" : "text-foreground",
                )}
              >
                {count(provider.none)}
              </Td>
              <Td
                className={cn(
                  "text-right tabular-nums",
                  provider.failures > 0 ? "font-medium text-critical" : "text-muted-foreground",
                )}
              >
                {count(provider.failures)}
              </Td>
              <Td
                className="whitespace-nowrap text-right tabular-nums"
                {...(provider.slowestMs === null
                  ? {}
                  : { title: `Najwolniejsze: ${duration(provider.slowestMs)}` })}
              >
                {duration(provider.p95Ms)}
              </Td>
            </Tr>
          )
        })}
      </tbody>
    </Table>
  )
}

const WIDTH = 112
const HEIGHT = 30
const PAD = 5

/**
 * The share answered locally, one point a day, on a fixed 0–100% scale so two
 * rows can be compared at a glance. A day the source answered nothing is a
 * gap, not a zero: nothing was asked, so nothing was answered remotely either.
 */
function Sparkline({ provider }: { provider: DashboardProvider }) {
  const days = provider.daily
  const ratios = days.map((day) => {
    const answered = day.calls - day.none
    return answered === 0 ? null : (answered - day.api) / answered
  })
  if (ratios.every((ratio) => ratio === null)) {
    return (
      <span className="text-[13px] text-muted-foreground">
        {provider.calls === 0 ? "brak wywołań" : "nie pytano"}
      </span>
    )
  }

  const x = (index: number) =>
    days.length === 1 ? WIDTH / 2 : PAD + (index / (days.length - 1)) * (WIDTH - 2 * PAD)
  const y = (ratio: number) => PAD + (1 - ratio) * (HEIGHT - 2 * PAD)

  // Runs of consecutive answered days; a gap breaks the line.
  const runs: { index: number; ratio: number }[][] = []
  ratios.forEach((ratio, index) => {
    if (ratio === null) return
    const run = runs.at(-1)
    const previous = run?.at(-1)
    if (run !== undefined && previous !== undefined && previous.index === index - 1) {
      run.push({ index, ratio })
    } else {
      runs.push([{ index, ratio }])
    }
  })
  const lastRun = runs.at(-1)
  const end = lastRun?.at(-1)
  const slot = WIDTH / days.length

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      className="block text-muted-foreground"
      role="img"
      aria-label={`${provider.title}: udział odpowiedzi lokalnych dzień po dniu`}
    >
      <line
        x1={PAD}
        x2={WIDTH - PAD}
        y1={y(0)}
        y2={y(0)}
        stroke="var(--border)"
        shapeRendering="crispEdges"
      />
      {runs.map((run) => {
        const line = run
          .map((point, at) => `${at === 0 ? "M" : "L"}${x(point.index)},${y(point.ratio)}`)
          .join("")
        const first = run[0]
        const last = run.at(-1)
        const area =
          first === undefined || last === undefined
            ? ""
            : `${line}L${x(last.index)},${y(0)}L${x(first.index)},${y(0)}Z`
        // A day with no answered neighbour is a point, not a line of no length.
        if (run.length === 1 && first !== undefined) {
          return (
            <circle
              key={first.index}
              cx={x(first.index)}
              cy={y(first.ratio)}
              r={1.5}
              fill="currentColor"
            />
          )
        }
        return (
          <g key={first?.index}>
            <path d={area} fill="currentColor" opacity={0.1} />
            <path
              d={line}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )
      })}
      {end !== undefined && (
        <circle
          cx={x(end.index)}
          cy={y(end.ratio)}
          r={3.5}
          fill="var(--foreground)"
          stroke="var(--card)"
          strokeWidth={2}
        />
      )}
      {/* A day per hit area, wider than the line, each with its own number. */}
      {days.map((day, index) => {
        const ratio = ratios[index]
        const answered = day.calls - day.none
        return (
          <rect
            key={day.date}
            x={index * slot}
            y={0}
            width={slot}
            height={HEIGHT}
            fill="transparent"
          >
            <title>
              {ratio === null || ratio === undefined
                ? `${dayMonth(day.date)}: nie pytano`
                : `${dayMonth(day.date)}: ${share(answered - day.api, answered)} lokalnie (${count(answered - day.api)} z ${count(answered)})`}
            </title>
          </rect>
        )
      })}
    </svg>
  )
}
